import KanbanAPI from '../../api/kanban';
import { captureConversationAccess } from 'dashboard/permissionSets/conversationReads';
import {
  captureKanbanAccess,
  kanbanEnabled,
  listedKanbanBoard,
  listedKanbanCards,
} from 'dashboard/permissionSets/kanbanReads';

const state = {
  readVersion: 0,
  lanes: [],
  meta: null,
  isLoading: false,
  error: null,
  boardKey: null,
  filters: {
    inboxId: null,
    assigneeId: null,
    teamId: null,
    priority: null,
    sortBy: 'last_activity_at_desc',
    searchQuery: null,
  },
  lanePagination: {},
  laneLoadingStates: {},
};

const getters = {
  getLanes: $state => $state.lanes,
  getMeta: $state => $state.meta,
  isLoading: $state => $state.isLoading,
  getError: $state => $state.error,
  getBoardKey: $state => $state.boardKey,
  hasBoardKey: $state => !!$state.boardKey,
  getFilters: $state => $state.filters,
  // Use lane.key as the internal identifier (immutable)
  getLanePagination: $state => laneKey =>
    $state.lanePagination[laneKey] || { page: 1, hasMore: true },
  isLaneLoading: $state => laneKey =>
    $state.laneLoadingStates[laneKey] || false,

  // Find lane by key (preferred) - key is immutable
  getLaneByKey: $state => key => {
    return $state.lanes.find(lane => lane.key === key);
  },

  // Find lane by name (for display purposes)
  getLaneByName: $state => name => {
    return $state.lanes.find(lane => lane.name === name);
  },

  getLaneById: $state => id => {
    return $state.lanes.find(lane => lane.id === id);
  },

  getTotalCards: $state => {
    return $state.lanes.reduce((sum, lane) => sum + lane.count, 0);
  },

  getCardById: $state => id => {
    let result = null;
    $state.lanes.some(lane => {
      const card = lane.conversations.find(c => c.id === id);
      if (card) {
        result = { card, lane };
        return true;
      }
      return false;
    });
    return result;
  },
};

const actions = {
  async fetchBoard(context) {
    const { commit, state: $state, rootState, rootGetters } = context;
    if (!$state.boardKey || !kanbanEnabled(rootGetters)) {
      commit('SET_LOADING', false);
      return;
    }

    commit('START_BOARD_READ');
    const version = $state.readVersion;
    const access = captureKanbanAccess(context);
    const isCurrent = () => access() && version === $state.readVersion;
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const params = {
        board_key: $state.boardKey,
        inbox_id: $state.filters.inboxId,
        assignee_id: $state.filters.assigneeId,
        team_id: $state.filters.teamId,
        priority: $state.filters.priority,
        sort_by: $state.filters.sortBy,
        q: $state.filters.searchQuery,
      };

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await KanbanAPI.getBoard(params);
      if (!isCurrent()) return;
      commit('SET_BOARD', listedKanbanBoard(rootState, response.data));
    } catch (error) {
      if (!isCurrent()) return;
      const errorMessage = error.response?.data?.error || error.message;
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      if (isCurrent()) commit('SET_LOADING', false);
    }
  },

  // Move card to a new lane using lane KEY (immutable)
  async moveCard(context, { conversationId, toLaneKey, position }) {
    const { commit, dispatch, state: $state, rootGetters } = context;
    const isCurrent = captureKanbanAccess(context);
    const access = captureConversationAccess(context);
    const boardKey = $state.boardKey;
    const canReconcile = () =>
      access() && boardKey === $state.boardKey && kanbanEnabled(rootGetters);
    // Get current stage KEY from card's custom_attributes
    const cardInfo = getters.getCardById($state)(conversationId);
    const currentStageKey =
      cardInfo?.card?.custom_attributes?.[$state.boardKey];

    // If card is already in target stage, do nothing
    if (currentStageKey === toLaneKey) {
      return;
    }

    // Backend expects display_id, not internal id
    const displayId = cardInfo?.card?.display_id;

    try {
      // API receives the stage KEY (immutable)
      await KanbanAPI.moveCard(displayId, toLaneKey, position, $state.boardKey);
    } catch (error) {
      if (!canReconcile()) return;
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        error.message;
      commit('SET_ERROR', errorMessage);
      dispatch('fetchBoard');
      throw error;
    }
    if (!canReconcile()) return;
    // A refresh may have read before this write committed. Reload its current
    // query instead of applying the old write snapshot over newer filters.
    if (!isCurrent()) {
      await dispatch('fetchBoard');
      return;
    }
    // Move the confirmed card between lanes
    commit('MOVE_CARD_OPTIMISTIC', {
      conversationId,
      fromLaneKey: currentStageKey,
      toLaneKey,
      position,
    });
  },

  async reorderCards(context, { laneKey, cards }) {
    const { commit, state: $state } = context;
    const isCurrent = captureKanbanAccess(context);
    const previousOrder = [...cards];

    commit('UPDATE_LANE_CARDS', { laneKey, cards });

    try {
      const cardPositions = cards.map((card, index) => ({
        id: card.id,
        position: index,
      }));

      await KanbanAPI.reorderCards(cardPositions, $state.boardKey);
    } catch (error) {
      if (!isCurrent()) return;
      commit('UPDATE_LANE_CARDS', {
        laneKey,
        cards: listedKanbanCards(context.rootState, previousOrder),
      });
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  setBoardKey({ commit, dispatch }, boardKey) {
    commit('SET_BOARD_KEY', boardKey);
    return dispatch('fetchBoard');
  },

  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    return dispatch('fetchBoard');
  },

  clearFilters({ commit, dispatch }) {
    commit('CLEAR_FILTERS');
    return dispatch('fetchBoard');
  },

  applySavedView({ commit, dispatch, rootGetters }, viewId) {
    const views = rootGetters['customViews/getKanbanCustomViews'] || [];
    const view = views.find(v => v.id === Number(viewId));
    if (!view) return undefined;
    commit('CLEAR_FILTERS');
    commit('SET_FILTERS', view.query || {});
    return dispatch('fetchBoard');
  },

  updateCard({ commit }, card) {
    commit('UPDATE_CARD', card);
  },

  removeCard({ commit }, conversationId) {
    commit('REMOVE_CARD', conversationId);
  },

  // Load more cards for a lane using the stage KEY (immutable)
  async loadMoreCards(context, laneKey) {
    const { commit, state: $state, rootState, rootGetters } = context;
    if (!kanbanEnabled(rootGetters)) return;
    const isCurrent = captureKanbanAccess(context);
    const pagination = $state.lanePagination[laneKey];
    if (!pagination?.hasMore || $state.laneLoadingStates[laneKey]) return;

    const nextPage = (pagination.page || 1) + 1;
    commit('SET_LANE_LOADING', { laneKey, isLoading: true });

    try {
      const params = {
        board_key: $state.boardKey,
        stage: laneKey, // Use stage KEY for API requests
        page: nextPage,
        inbox_id: $state.filters.inboxId,
        assignee_id: $state.filters.assigneeId,
        team_id: $state.filters.teamId,
        priority: $state.filters.priority,
        sort_by: $state.filters.sortBy,
        q: $state.filters.searchQuery,
      };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);

      const response = await KanbanAPI.getLaneCards(params);
      if (!isCurrent()) return;
      commit('APPEND_LANE_CARDS', {
        laneKey,
        conversations: listedKanbanCards(
          rootState,
          response.data.conversations
        ),
      });
      commit('SET_LANE_PAGINATION', {
        laneKey,
        pagination: {
          page: nextPage,
          hasMore: response.data.pagination.has_more,
        },
      });
    } catch (error) {
      if (!isCurrent()) return;
      const errorMessage = error.response?.data?.error || error.message;
      commit('SET_ERROR', errorMessage);
    } finally {
      if (isCurrent())
        commit('SET_LANE_LOADING', { laneKey, isLoading: false });
    }
  },
};

const mutations = {
  START_BOARD_READ($state) {
    $state.readVersion += 1;
    $state.laneLoadingStates = {};
  },

  CLEAR_CONVERSATION_ACCESS($state) {
    $state.readVersion += 1;
    $state.lanes = [];
    $state.meta = null;
    $state.error = null;
    $state.isLoading = false;
    $state.lanePagination = {};
    $state.laneLoadingStates = {};
  },

  REVOKE_CONVERSATION_ACCESS($state, displayId) {
    $state.lanes.forEach(lane => {
      const count = lane.conversations.length;
      lane.conversations = lane.conversations.filter(
        card => Number(card.display_id) !== Number(displayId)
      );
      const removed = count - lane.conversations.length;
      lane.count = Math.max(0, lane.count - removed);
      if ($state.meta)
        $state.meta.total_conversations = Math.max(
          0,
          $state.meta.total_conversations - removed
        );
    });
  },

  SET_LOADING($state, isLoading) {
    $state.isLoading = isLoading;
  },

  SET_ERROR($state, error) {
    $state.error = error;
  },

  SET_BOARD($state, { lanes, meta }) {
    $state.lanes = lanes;
    $state.meta = meta;
    $state.error = null;
    $state.lanePagination = {};
    $state.laneLoadingStates = {};
    // Use lane.key as the internal identifier (immutable)
    lanes.forEach(lane => {
      $state.lanePagination[lane.key] = {
        page: lane.pagination?.current_page || 1,
        hasMore: lane.pagination?.has_more ?? true,
      };
      $state.laneLoadingStates[lane.key] = false;
    });
  },

  SET_BOARD_KEY($state, boardKey) {
    $state.boardKey = boardKey;
  },

  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },

  CLEAR_FILTERS($state) {
    $state.filters = {
      inboxId: null,
      assigneeId: null,
      teamId: null,
      priority: null,
      sortBy: 'last_activity_at_desc',
      searchQuery: null,
    };
  },

  UPDATE_LANE_COUNT($state, { laneKey, delta }) {
    const lane = $state.lanes.find(l => l.key === laneKey);
    if (lane) {
      lane.count = Math.max(0, lane.count + delta);
    }
  },

  // Move card between lanes using lane KEYS (immutable identifiers)
  MOVE_CARD_OPTIMISTIC(
    $state,
    { conversationId, fromLaneKey, toLaneKey, position }
  ) {
    const fromLaneObj = $state.lanes.find(l => l.key === fromLaneKey);
    const toLaneObj = $state.lanes.find(l => l.key === toLaneKey);

    if (!fromLaneObj || !toLaneObj) return;

    const cardIndex = fromLaneObj.conversations.findIndex(
      c => c.id === conversationId
    );

    if (cardIndex === -1) return;

    const [card] = fromLaneObj.conversations.splice(cardIndex, 1);
    fromLaneObj.count = Math.max(0, fromLaneObj.count - 1);

    // Store the stage KEY (not name) in custom_attributes
    card.custom_attributes = {
      ...card.custom_attributes,
      [$state.boardKey]: toLaneKey,
    };

    if (position !== null && position !== undefined && position >= 0) {
      toLaneObj.conversations.splice(position, 0, card);
    } else {
      toLaneObj.conversations.push(card);
    }
    toLaneObj.count += 1;
  },

  UPDATE_LANE_CARDS($state, { laneKey, cards }) {
    const lane = $state.lanes.find(l => l.key === laneKey);
    if (lane) {
      lane.conversations = cards;
    }
  },

  UPDATE_CARD($state, updatedCard) {
    $state.lanes.some(lane => {
      const index = lane.conversations.findIndex(c => c.id === updatedCard.id);
      if (index !== -1) {
        // custom_attributes stores the stage KEY (not name)
        const cardStageKey = updatedCard.custom_attributes?.[$state.boardKey];
        if (cardStageKey && cardStageKey !== lane.key) {
          lane.conversations.splice(index, 1);
          lane.count -= 1;

          const newLane = $state.lanes.find(l => l.key === cardStageKey);
          if (newLane) {
            newLane.conversations.push(updatedCard);
            newLane.count += 1;
          }
        } else {
          lane.conversations.splice(index, 1, updatedCard);
        }
        return true;
      }
      return false;
    });
  },

  REMOVE_CARD($state, conversationId) {
    $state.lanes.some(lane => {
      const index = lane.conversations.findIndex(c => c.id === conversationId);
      if (index !== -1) {
        lane.conversations.splice(index, 1);
        lane.count = Math.max(0, lane.count - 1);
        return true;
      }
      return false;
    });
  },

  SET_LANE_LOADING($state, { laneKey, isLoading }) {
    $state.laneLoadingStates = {
      ...$state.laneLoadingStates,
      [laneKey]: isLoading,
    };
  },

  SET_LANE_PAGINATION($state, { laneKey, pagination }) {
    $state.lanePagination = {
      ...$state.lanePagination,
      [laneKey]: pagination,
    };
  },

  APPEND_LANE_CARDS($state, { laneKey, conversations }) {
    const lane = $state.lanes.find(l => l.key === laneKey);
    if (lane) {
      lane.conversations = [...lane.conversations, ...conversations];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
